import { useCallback, useMemo, useRef, useState } from 'react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { SearchResultLink, ClickableNonLink } from './styled';
import loadable from '@loadable/component';
import { useKeys } from '@/hooks/useKeys';
import type { Theme } from '@simplywallst/ui-core';
/** redux stuff */
import { useDispatch } from 'react-redux';
import { cacheResults } from '@/components/Search/redux/routines';
import { pathToAction } from 'redux-first-router';
import routesGenerator from '@/router/routesGenerator';
import { isSupportedLocale } from '@/utils/locale';
import { ROUTE_LOCALE_COMPANY } from '@/constants/routes';
/** */

const AlgoliaSearch = loadable(
  () => import('@/components/Search/components/AlgoliaSearch'),
  { ssr: false }
);

interface Props {
  value?: string;
  onClick?: (uniqueSymbol: string) => void;
  onSelectCompany?: (company: { id: string; name: string }) => void;
  query?: string;
  placeholder?: string;
  /** Input field name */
  name?: string;
  onClose?: () => void;
  defaultState?: ReactNode;
  theme?: Theme['appTheme'];
  onReset?: () => void;
  onBlur?: (value: string) => void;
  error?: string;
  locale?: string;
}
type CachedResults = {
  name: string;
  url: string;
  uniqueSymbol: string;
};

const routesMap = routesGenerator();

export const BasicSearch = ({
  value,
  onClick,
  onSelectCompany,
  query,
  placeholder,
  name,
  onClose,
  defaultState,
  theme,
  onReset,
  onBlur,
  error,
  locale = 'en',
}: Props) => {
  const inputRef = useRef<null | HTMLInputElement>(null);
  const dispatch = useDispatch();
  const keys = useMemo(() => ['ArrowUp', 'ArrowDown', 'Enter'] as const, []);
  const cachedResults = useRef<CachedResults[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  useKeys({
    targetKeys: keys,
    targetRef: inputRef.current,
    preventDefaut: true,
    callback(keyPress) {
      if (!open) return; // noop;
      if (keyPress === 'ArrowDown') {
        if (activeIndex + 1 === cachedResults.current?.length) {
          setActiveIndex(0);
        } else {
          setActiveIndex(activeIndex + 1);
        }
        return;
      }
      if (keyPress === 'ArrowUp') {
        if (activeIndex - 1 < 0) {
          if (typeof cachedResults.current?.length === 'number') {
            setActiveIndex(cachedResults.current?.length - 1);
          }
        } else {
          setActiveIndex(activeIndex - 1);
        }
        return;
      }
      if (keyPress === 'Enter' && cachedResults.current) {
        try {
          const resolvedActive = activeIndex === -1 ? 0 : activeIndex;
          if (
            typeof onClick === 'undefined' &&
            typeof onSelectCompany === 'undefined'
          ) {
            /** redirect to company if `onClick` and `getCompanyDetails` are undefined */
            const action = pathToAction(
              cachedResults.current[resolvedActive].url,
              routesMap
            );
            dispatch(action);
          } else {
            const { name, uniqueSymbol } =
              cachedResults.current[resolvedActive];
            onClick?.(uniqueSymbol);
            onSelectCompany?.({ id: uniqueSymbol, name });
          }
          if (inputRef.current) {
            inputRef.current.blur();
          }
        } catch (error) {
          console.warn(error);
        }
      }
    },
  });

  /**
   * Wrap callback because this component has a high frequency of re-render
   * because of the keyboard listeners
   */

  const primaryWrapperHandler = useCallback<
    NonNullable<
      ComponentPropsWithoutRef<typeof AlgoliaSearch>['primaryWrapper']
    >
  >(
    (child, { listIndex, name, canonicalUrl, uniqueSymbol }) => {
      cachedResults.current[listIndex] = {
        name,
        url: canonicalUrl,
        uniqueSymbol,
      };
      if (
        typeof onClick === 'function' ||
        typeof onSelectCompany === 'function'
      ) {
        return (
          <ClickableNonLink
            onClick={() => {
              onClick?.(uniqueSymbol);
              onSelectCompany?.({ id: uniqueSymbol, name });
            }}
          >
            {child}
          </ClickableNonLink>
        );
      }
      if (isSupportedLocale(locale)) {
        const targetRoute = pathToAction(canonicalUrl, routesMap);
        const payloadWithLocale = {
          ...targetRoute,
          type: ROUTE_LOCALE_COMPANY,
          payload: {
            ...targetRoute.payload,
            locale,
          },
        };
        return (
          <SearchResultLink to={payloadWithLocale} tabIndex={0}>
            {child}
          </SearchResultLink>
        );
      }
      return (
        <SearchResultLink to={canonicalUrl} tabIndex={0}>
          {child}
        </SearchResultLink>
      );
    },

    [onClick, onSelectCompany, locale]
  );

  const secondaryWrapperHandler = useCallback<
    NonNullable<
      ComponentPropsWithoutRef<typeof AlgoliaSearch>['secondaryWrapper']
    >
  >(
    (child, { uniqueSymbol, canonicalUrl }) => {
      if (
        typeof onClick === 'function' ||
        typeof onSelectCompany === 'function'
      ) {
        return (
          <ClickableNonLink
            onClick={() => {
              onClick?.(uniqueSymbol);
              onSelectCompany?.({
                id: uniqueSymbol,
                name: uniqueSymbol,
              });
            }}
          >
            {child}
          </ClickableNonLink>
        );
      }

      if (isSupportedLocale(locale)) {
        const targetRoute = pathToAction(canonicalUrl, routesMap);
        const payloadWithLocale = {
          ...targetRoute,
          type: ROUTE_LOCALE_COMPANY,
          payload: {
            ...targetRoute.payload,
            locale,
          },
        };
        return (
          <SearchResultLink to={payloadWithLocale} tabIndex={0}>
            {child}
          </SearchResultLink>
        );
      }

      return (
        <SearchResultLink tabIndex={0} to={canonicalUrl}>
          {child}
        </SearchResultLink>
      );
    },
    [onClick, onSelectCompany, locale]
  );

  return (
    <AlgoliaSearch
      activeIndex={activeIndex}
      onSearchResult={(results) => {
        cachedResults.current = [];
        dispatch(cacheResults.trigger(results));
      }}
      onOpen={(open, ref) => {
        inputRef.current = ref.current;
        setOpen(open);
        if (!open) {
          // reset active index
          setActiveIndex(-1);
        }
      }}
      onClose={onClose}
      name={name}
      placeholder={placeholder}
      query={query}
      primaryWrapper={primaryWrapperHandler}
      secondaryWrapper={secondaryWrapperHandler}
      defaultState={defaultState}
      theme={theme}
      onReset={onReset}
      onBlur={onBlur}
      value={value}
      error={error}
    />
  );
};
